/**
 * SH Filter Values Enhancement
 *
 * Purpose: Fetch and inject all filter values (Make/Model/Year) beyond Shopify's 100-value limit
 * Strategy: Non-blocking progressive enhancement using Storefront API with pagination
 *
 * Flow:
 * 1. Page loads with first 100 values from Liquid (immediate display)
 * 2. This script runs after DOM ready (deferred, non-blocking)
 * 3. Fetches all products in batches of 250 (~35 requests for 8,753 products)
 * 4. Deduplicates and injects missing values progressively
 * 5. Maintains compatibility with existing cascading filter logic in shop.js.liquid
 */

// ============================================================================
// CONFIGURATION & CONSTANTS
// ============================================================================

const CONFIG = {
  API_ENDPOINT: 'https://autobeam.myshopify.com/api/2024-10/graphql.json',
  ACCESS_TOKEN: '30d79f7bcae44071975736d49c1e876c',
  BATCH_SIZE: 250, // Products per request (Storefront API max)
  PROGRESSIVE_INJECT_INTERVAL: 5, // Inject DOM updates every N batches
  CACHE_TTL: 3600000, // 1 hour in milliseconds
  RETRY_MAX_ATTEMPTS: 3,
  RETRY_DELAY_MS: 1000,
  DEBUG: true, // Set to false in production
  CACHE_VERSION: 4 // Increment to invalidate old cache (fix for desktop/mobile selector specificity)
};

const FILTER_CONFIG = {
  make: {
    metafieldKey: 'make',
    paramName: 'filter.p.m.custom.make',
    dataLabel: 'make'
  },
  model: {
    metafieldKey: 'models',
    paramName: 'filter.p.m.custom.models',
    dataLabel: 'model'
  },
  year: {
    metafieldKey: 'year',
    paramName: 'filter.p.m.custom.year',
    dataLabel: 'year'
  }
};

// ============================================================================
// DEBUG UTILITIES
// ============================================================================

/**
 * Centralized debug logging
 */
const logger = {
  log: (...args) => {
    if (CONFIG.DEBUG) console.log('[SH Filter Values]', ...args);
  },
  error: (...args) => {
    console.error('[SH Filter Values ERROR]', ...args);
  },
  warn: (...args) => {
    console.warn('[SH Filter Values WARN]', ...args);
  },
  group: (label) => {
    if (CONFIG.DEBUG) console.group(`[SH Filter Values] ${label}`);
  },
  groupEnd: () => {
    if (CONFIG.DEBUG) console.groupEnd();
  }
};

// ============================================================================
// API CLIENT
// ============================================================================

/**
 * GraphQL query to fetch product metafields with pagination
 */
const GRAPHQL_QUERY = `
  query GetProductMetafields($id: ID!, $cursor: String) {
    collection(id: $id) {
      products(first: ${CONFIG.BATCH_SIZE}, after: $cursor) {
        edges {
          node {
            id
            make: metafield(namespace: "custom", key: "make") {
              value
            }
            models: metafield(namespace: "custom", key: "models") {
              value
            }
            year: metafield(namespace: "custom", key: "year") {
              value
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

/**
 * Fetch data from Storefront API with retry logic
 * @param {Object} variables - GraphQL variables
 * @param {number} attempt - Current retry attempt
 * @returns {Promise<Object>} API response data
 */
async function fetchGraphQL(variables, attempt = 1) {
  try {
    const response = await fetch(CONFIG.API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': CONFIG.ACCESS_TOKEN
      },
      body: JSON.stringify({
        query: GRAPHQL_QUERY,
        variables: variables
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    if (data.errors) {
      throw new Error(`GraphQL Error: ${JSON.stringify(data.errors)}`);
    }

    return data;

  } catch (error) {
    logger.error(`Fetch attempt ${attempt}/${CONFIG.RETRY_MAX_ATTEMPTS} failed:`, error.message);

    if (attempt < CONFIG.RETRY_MAX_ATTEMPTS) {
      const delay = CONFIG.RETRY_DELAY_MS * attempt;
      logger.log(`Retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return fetchGraphQL(variables, attempt + 1);
    }

    throw error;
  }
}

// ============================================================================
// CACHE MANAGEMENT
// ============================================================================

/**
 * Get cached filter values from sessionStorage
 * @param {number} collectionId - Shopify collection ID
 * @returns {Object|null} Cached data or null if expired/not found
 */
function getCachedValues(collectionId) {
  try {
    const cacheKey = `sh-filter-values-${collectionId}`;
    const cached = sessionStorage.getItem(cacheKey);

    if (!cached) {
      logger.log('No cache found');
      return null;
    }

    const parsedCache = JSON.parse(cached);
    const { data, timestamp, version } = parsedCache;

    // Invalidate cache if version doesn't match
    if (version !== CONFIG.CACHE_VERSION) {
      logger.log(`Cache version mismatch (cached: ${version}, current: ${CONFIG.CACHE_VERSION}) - invalidating`);
      sessionStorage.removeItem(cacheKey);
      return null;
    }

    const age = Date.now() - timestamp;

    if (age < CONFIG.CACHE_TTL) {
      logger.log(`Cache hit (age: ${Math.round(age / 1000)}s, version: ${version})`);
      return data;
    }

    logger.log('Cache expired');
    sessionStorage.removeItem(cacheKey);
    return null;

  } catch (error) {
    logger.warn('Cache read error:', error.message);
    return null;
  }
}

/**
 * Save filter values to sessionStorage
 * @param {number} collectionId - Shopify collection ID
 * @param {Object} data - Filter values to cache
 */
function setCachedValues(collectionId, data) {
  try {
    const cacheKey = `sh-filter-values-${collectionId}`;
    sessionStorage.setItem(cacheKey, JSON.stringify({
      data: data,
      timestamp: Date.now(),
      version: CONFIG.CACHE_VERSION
    }));
    logger.log(`Values cached successfully (version: ${CONFIG.CACHE_VERSION})`);
  } catch (error) {
    logger.warn('Cache write error:', error.message);
  }
}

// ============================================================================
// DATA PROCESSING
// ============================================================================

/**
 * Extract existing filter values from DOM
 * @param {string} filterType - 'make', 'model', or 'year'
 * @param {string} target - 'desktop', 'mobile', or 'all' (default: 'all')
 * @returns {Set<string>|number} Set of existing values or count if target specified
 */
function getExistingValues(filterType, target = 'all') {
  const config = FILTER_CONFIG[filterType];
  if (!config) return target === 'all' ? new Set() : 0;

  let selector;
  if (target === 'desktop') {
    selector = `.se-mainselect:not(.se-mobileview)[data-label="${config.dataLabel}"] .collection-nav label`;
  } else if (target === 'mobile') {
    selector = `.se-mobileview[data-label="${config.dataLabel}"] .collection-nav label`;
  } else {
    // Get from both and deduplicate
    selector = `[data-label="${config.dataLabel}"] .collection-nav label`;
  }

  const values = new Set();
  let count = 0;

  document.querySelectorAll(selector).forEach(label => {
    const text = label.textContent.trim();
    if (text && text.toLowerCase() !== 'all') {
      values.add(text);
      count++;
    }
  });

  if (target === 'all') {
    logger.log(`Found ${values.size} existing ${filterType} values in DOM (deduplicated from desktop + mobile)`);
    return values;
  } else {
    logger.log(`Found ${count} existing ${filterType} values in ${target} DOM`);
    return count;
  }
}

/**
 * Parse metafield values (handles both JSON arrays and comma-separated strings)
 * @param {string} value - Metafield value (may be JSON array like ["Value"] or comma-separated string)
 * @returns {Array<string>} Array of trimmed values
 */
function parseMetafieldValue(value) {
  if (!value) return [];

  // Try parsing as JSON first (handles ["Value"] or "Value" format from Shopify metafields)
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed.map(v => String(v).trim()).filter(v => v.length > 0);
    }
    // If parsed to single value, return as array
    return [String(parsed).trim()].filter(v => v.length > 0);
  } catch (e) {
    // Fall back to comma-split for plain text values like "value1, value2"
    return value.split(',').map(v => v.trim()).filter(v => v.length > 0);
  }
}

/**
 * Process product edges and extract unique filter values
 * @param {Array} edges - GraphQL product edges
 * @param {Set} makeSet - Set to accumulate make values
 * @param {Set} modelSet - Set to accumulate model values
 * @param {Set} yearSet - Set to accumulate year values
 * @returns {Object} Statistics about products without metafields
 */
function processProductBatch(edges, makeSet, modelSet, yearSet) {
  let sampleLogged = false;
  let withoutMake = 0;
  let withoutModel = 0;
  let withoutYear = 0;

  edges.forEach(({ node }, index) => {
    // Log first product's raw metafield values for debugging
    if (!sampleLogged && (node.make?.value || node.models?.value || node.year?.value)) {
      logger.log('Sample raw metafield values from product:', {
        productId: node.id,
        makeRaw: node.make?.value,
        makeParsed: node.make?.value ? parseMetafieldValue(node.make.value) : null,
        modelsRaw: node.models?.value,
        modelsParsed: node.models?.value ? parseMetafieldValue(node.models.value) : null,
        yearRaw: node.year?.value,
        yearParsed: node.year?.value ? parseMetafieldValue(node.year.value) : null
      });
      sampleLogged = true;
    }

    // Make (typically single value)
    if (node.make?.value) {
      parseMetafieldValue(node.make.value).forEach(v => makeSet.add(v));
    } else {
      withoutMake++;
    }

    // Models (often comma-separated)
    if (node.models?.value) {
      parseMetafieldValue(node.models.value).forEach(v => modelSet.add(v));
    } else {
      withoutModel++;
    }

    // Year (often comma-separated)
    if (node.year?.value) {
      parseMetafieldValue(node.year.value).forEach(v => yearSet.add(v));
    } else {
      withoutYear++;
    }
  });

  return { withoutMake, withoutModel, withoutYear };
}

/**
 * Sort filter values (alphabetically or numerically)
 * @param {Array<string>} values - Values to sort
 * @param {string} filterType - 'make', 'model', or 'year'
 * @returns {Array<string>} Sorted values
 */
function sortFilterValues(values, filterType) {
  if (filterType === 'year') {
    // Numeric sort for years
    return values.sort((a, b) => {
      const numA = parseInt(a);
      const numB = parseInt(b);
      if (!isNaN(numA) && !isNaN(numB)) return numB - numA; // Descending
      return a.localeCompare(b);
    });
  }

  // Alphabetical sort for make/model
  return values.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

// ============================================================================
// DOM MANIPULATION
// ============================================================================

/**
 * Inject new filter values into DOM (desktop and mobile)
 * @param {string} filterType - 'make', 'model', or 'year'
 * @param {Set} allValues - All available values
 */
function injectNewValues(filterType, allValues) {
  const existingValues = getExistingValues(filterType);
  const newValues = [...allValues].filter(v => !existingValues.has(v));

  if (newValues.length === 0) {
    logger.log(`No new ${filterType} values to inject`);
    return;
  }

  logger.log(`Injecting ${newValues.length} new ${filterType} values`);
  logger.log(`Sample new ${filterType} values:`, newValues.slice(0, 10));

  const sortedValues = sortFilterValues(newValues, filterType);

  injectIntoDesktop(filterType, sortedValues, existingValues.size);
  injectIntoMobile(filterType, sortedValues, existingValues.size);

  logger.log(`Successfully injected ${newValues.length} ${filterType} values`);
}

/**
 * Inject values into desktop filter dropdown
 * @param {string} filterType - 'make', 'model', or 'year'
 * @param {Array<string>} newValues - Sorted new values to inject
 * @param {number} existingCount - Count of existing values (for ID generation)
 */
function injectIntoDesktop(filterType, newValues, existingCount) {
  const config = FILTER_CONFIG[filterType];
  // Desktop uses se-mainselect but NOT se-mobileview
  const container = document.querySelector(`.se-mainselect:not(.se-mobileview)[data-label="${config.dataLabel}"] .collection-nav`);

  if (!container) {
    logger.warn(`Desktop container not found for ${filterType}`);
    return;
  }

  // Get desktop-specific existing count for proper ID generation
  const desktopExistingCount = getExistingValues(filterType, 'desktop');

  const fragment = document.createDocumentFragment();

  newValues.forEach((value, idx) => {
    const index = desktopExistingCount + idx + 1;

    const li = document.createElement('li');
    const label = document.createElement('label');
    const input = document.createElement('input');

    input.type = 'radio';
    input.name = config.paramName;
    input.value = value;
    input.id = `Desktop-Filter-${config.paramName}-${index}`;

    label.setAttribute('for', input.id);
    label.appendChild(input);
    label.appendChild(document.createTextNode(value));

    li.appendChild(label);
    fragment.appendChild(li);
  });

  container.appendChild(fragment);
}

/**
 * Inject values into mobile filter dropdown
 * @param {string} filterType - 'make', 'model', or 'year'
 * @param {Array<string>} newValues - Sorted new values to inject
 * @param {number} existingCount - Count of existing values (for ID generation)
 */
function injectIntoMobile(filterType, newValues, existingCount) {
  const config = FILTER_CONFIG[filterType];

  // Mobile uses se-mobileview class to differentiate from desktop
  const mobileContainer = document.querySelector(`.se-mobileview[data-label="${config.dataLabel}"] .collection-nav`);

  if (!mobileContainer) {
    logger.warn(`Mobile container not found for ${filterType}`);
    return;
  }

  // Get mobile-specific existing count for proper ID generation
  const mobileExistingCount = getExistingValues(filterType, 'mobile');

  const fragment = document.createDocumentFragment();

  newValues.forEach((value, idx) => {
    const index = mobileExistingCount + idx + 1;

    const li = document.createElement('li');
    const label = document.createElement('label');
    const input = document.createElement('input');

    input.type = 'radio';
    input.name = config.paramName;
    input.value = value;
    input.id = `Filter-${config.paramName}-${index}`;

    label.setAttribute('for', input.id);
    label.appendChild(input);
    label.appendChild(document.createTextNode(value));

    li.appendChild(label);
    fragment.appendChild(li);
  });

  mobileContainer.appendChild(fragment);
}

// ============================================================================
// MAIN ORCHESTRATOR
// ============================================================================

/**
 * Main function: Fetch all filter values with pagination
 * @param {number} collectionId - Shopify collection ID
 */
async function fetchAllFilterValues(collectionId) {
  const startTime = Date.now();
  logger.group('Fetching All Filter Values');
  logger.log(`Collection ID: ${collectionId}`);

  try {
    // Check cache first
    const cached = getCachedValues(collectionId);
    if (cached) {
      logger.log('Using cached values');
      logger.log('Cached Make values:', cached.make.sort());
      logger.log('Cached Model values:', cached.model.sort());
      logger.log('Cached Year values:', cached.year.sort());
      injectNewValues('make', new Set(cached.make));
      injectNewValues('model', new Set(cached.model));
      injectNewValues('year', new Set(cached.year));
      logger.groupEnd();
      return;
    }

    // Initialize Sets with existing DOM values for deduplication
    const makeSet = getExistingValues('make');
    const modelSet = getExistingValues('model');
    const yearSet = getExistingValues('year');

    let cursor = null;
    let requestCount = 0;
    let totalProducts = 0;
    let productsWithoutMake = 0;
    let productsWithoutModel = 0;
    let productsWithoutYear = 0;

    // Paginate through all products
    do {
      logger.log(`Fetching batch ${requestCount + 1} (cursor: ${cursor ? 'yes' : 'initial'})`);

      const data = await fetchGraphQL({
        id: `gid://shopify/Collection/${collectionId}`,
        cursor: cursor
      });

      const { edges, pageInfo } = data.data.collection.products;

      requestCount++;
      totalProducts += edges.length;

      // Process batch and deduplicate in real-time
      const batchStats = processProductBatch(edges, makeSet, modelSet, yearSet);
      productsWithoutMake += batchStats.withoutMake;
      productsWithoutModel += batchStats.withoutModel;
      productsWithoutYear += batchStats.withoutYear;

      logger.log(`Batch ${requestCount}: Processed ${edges.length} products (Total: ${totalProducts})`);
      logger.log(`Current totals - Make: ${makeSet.size}, Model: ${modelSet.size}, Year: ${yearSet.size}`);

      // Progressive injection every N batches to reduce perceived lag
      if (requestCount % CONFIG.PROGRESSIVE_INJECT_INTERVAL === 0) {
        logger.log('Progressive injection triggered');
        injectNewValues('make', makeSet);
        injectNewValues('model', modelSet);
        injectNewValues('year', yearSet);
      }

      cursor = pageInfo.hasNextPage ? pageInfo.endCursor : null;

    } while (cursor);

    // Final injection of any remaining values
    logger.log('Final injection');
    injectNewValues('make', makeSet);
    injectNewValues('model', modelSet);
    injectNewValues('year', yearSet);

    // Cache results
    const cacheData = {
      make: [...makeSet].sort(),
      model: [...modelSet].sort(),
      year: [...yearSet].sort()
    };

    setCachedValues(collectionId, cacheData);

    const duration = ((Date.now() - startTime) / 1000).toFixed(2);
    logger.log(`✓ Complete! Fetched ${totalProducts} products in ${requestCount} requests (${duration}s)`);
    logger.log(`Final totals - Make: ${makeSet.size}, Model: ${modelSet.size}, Year: ${yearSet.size}`);
    logger.log(`Products without metafields - Make: ${productsWithoutMake}, Model: ${productsWithoutModel}, Year: ${productsWithoutYear}`);
    logger.log('All Make values:', cacheData.make);
    logger.log('All Model values:', cacheData.model);
    logger.log('All Year values:', cacheData.year);

  } catch (error) {
    logger.error('Failed to fetch filter values:', error.message);
    logger.error('Stack:', error.stack);
  } finally {
    logger.groupEnd();
  }
}

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize filter enhancement when DOM is ready
 */
(function init() {
  // Wait for DOM to be fully loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startEnhancement);
  } else {
    startEnhancement();
  }

  function startEnhancement() {
    logger.log('Initializing filter enhancement');

    // Get collection ID from page
    const collectionDataElement = document.querySelector('[data-collection-id]');

    if (!collectionDataElement) {
      logger.warn('Collection ID element not found - skipping enhancement');
      return;
    }

    try {
      const collectionData = JSON.parse(collectionDataElement.textContent);
      const collectionId = parseInt(collectionData.collection_id);

      if (!collectionId || isNaN(collectionId)) {
        logger.warn('Invalid collection ID - skipping enhancement');
        return;
      }

      logger.log(`Starting enhancement for collection ${collectionId}`);
      fetchAllFilterValues(collectionId);

    } catch (error) {
      logger.error('Failed to parse collection ID:', error.message);
    }
  }
})();
